import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  faArrowDown,
  faArrowsUpDown,
  faArrowUp,
  faCog,
  faPlus,
  faServer,
} from "@fortawesome/pro-regular-svg-icons";
import { createColumnHelper, HeaderContext } from "@tanstack/react-table";
import { Link, useSearchParams } from "react-router-dom";

import { ModuleConfigModal } from "src/adminPanel/modules/ModuleConfigModal";
import { ModuleCreateModal } from "src/adminPanel/modules/ModuleCreateModal";
import { ModulesVersionBar } from "src/adminPanel/modules/ModulesVersionBar";
import { useUserOrganizations } from "src/api/queries";
import { ModuleSpec, useWorkspaces } from "src/api/workspacesV2Api";
import { Button } from "src/base-components/Button";
import { Icon } from "src/base-components/Icon";
import { Input } from "src/base-components/Input";
import { LoadingView } from "src/base-components/LoadingView";
import { Pill } from "src/base-components/Pill";
import { Switch } from "src/base-components/Switch";
import { Cell, Header, TableComp } from "src/base-components/Table";
import { Tabs } from "src/base-components/Tabs";
import { ModuleStatus, WorkspaceDb } from "src/clients/flow-api";
import { OrganizationInDB } from "src/clients/taktile-api";
import { EmptyState } from "src/design-system/EmptyState";
import { useModal } from "src/design-system/Modal";

export const getModuleDisplayName = (moduleType: string) => {
  return moduleType.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
};

const HighlightText: React.FC<{ text: string; searchTerm: string }> = ({
  text,
  searchTerm,
}) => {
  if (!searchTerm) return text;

  const parts = text.split(new RegExp(`(${searchTerm})`, "gi"));
  return parts.map((part, i) =>
    part.toLowerCase() === searchTerm.toLowerCase() ? (
      <span key={i} className="bg-yellow-200">
        {part}
      </span>
    ) : (
      part
    ),
  );
};

const getStatusColor = (status: ModuleStatus) => {
  if (status.toLowerCase().includes("complete")) {
    return "text-green-600 bg-green-50 border-green-200";
  } else if (status.toLowerCase().includes("in_progress")) {
    return "text-yellow-600 bg-yellow-50 border-yellow-200";
  } else if (status.toLowerCase().includes("failed")) {
    return "text-red-600 bg-red-50 border-red-200";
  } else {
    return "text-gray-600 bg-gray-50 border-gray-200";
  }
};

const ModuleSection: React.FC<{
  moduleType: string;
  workspaces: WorkspaceDb[];
  orgMap: Map<string, OrganizationInDB>;
  searchTerm: string;
  allVersions: string[];
}> = ({ moduleType, workspaces, orgMap, searchTerm, allVersions }) => {
  const {
    isOpen: configModalOpen,
    openModal: openConfigModal,
    closeModal: closeConfigModal,
    afterLeave: configAfterLeave,
    data: selectedWorkspace,
  } = useModal<WorkspaceDb>();
  const {
    isOpen: createModalOpen,
    openModal: openCreateModal,
    closeModal: closeCreateModal,
    afterLeave,
  } = useModal();
  const [selectedModule, setSelectedModule] = useState<ModuleSpec | null>(null);
  const [selectedVersion, setSelectedVersion] = useState<string | null>(null);

  const handleConfigureModule = (workspace: WorkspaceDb) => {
    const modules = workspace.modules as Record<string, ModuleSpec>;
    const module = modules?.[moduleType];
    if (module) {
      setSelectedModule(module);
      openConfigModal(workspace);
    }
  };

  const handleModalClose = () => {
    closeConfigModal();
    setSelectedModule(null);
  };

  // Filter workspaces that have this module created
  const workspacesWithModule = workspaces.filter((ws) => {
    const modules = ws.modules as Record<string, ModuleSpec>;
    return modules?.[moduleType];
  });

  // Apply search and version filters
  const filteredWorkspaces = workspacesWithModule.filter((ws) => {
    // Apply version filter
    if (selectedVersion) {
      const modules = ws.modules as Record<string, ModuleSpec>;
      const module = modules?.[moduleType];
      if (module?.version !== selectedVersion) {
        return false;
      }
    }

    // Apply search filter
    if (!searchTerm) return true;

    const org = orgMap.get(ws.organization_id);
    const searchLower = searchTerm.toLowerCase();

    return (
      ws.name.toLowerCase().includes(searchLower) ||
      ws.slug.toLowerCase().includes(searchLower) ||
      ws.id.toLowerCase().includes(searchLower) ||
      ws.organization_id.toLowerCase().includes(searchLower) ||
      (org?.name?.toLowerCase().includes(searchLower) ?? false)
    );
  });

  const renderContent = () => {
    if (filteredWorkspaces.length === 0) {
      return (
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-gray-50 shadow-sm">
          <EmptyState
            actionButton={{
              text: "Create Module",
              icon: faPlus,
              onClick: openCreateModal,
            }}
            description={
              searchTerm
                ? `No workspaces matching "${searchTerm}" have this module created.`
                : `No workspaces have this module created yet.`
            }
            headline="No workspaces found"
            icon={faServer}
          />
        </div>
      );
    }

    return (
      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left font-inter-semibold-12px text-gray-500 uppercase tracking-wider">
                  Organization
                </th>
                <th className="px-4 py-3 text-left font-inter-semibold-12px text-gray-500 uppercase tracking-wider">
                  Workspace
                </th>
                <th className="px-4 py-3 text-left font-inter-semibold-12px text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-left font-inter-semibold-12px text-gray-500 uppercase tracking-wider">
                  Version
                </th>
                <th className="px-4 py-3 text-left font-inter-semibold-12px text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {filteredWorkspaces.map((workspace) => {
                const modules = workspace.modules as Record<string, ModuleSpec>;
                const module = modules?.[moduleType];
                if (!module) return null;

                return (
                  <tr
                    key={workspace.id}
                    className="transition-colors hover:bg-gray-50"
                  >
                    <td className="whitespace-nowrap px-4 py-3 font-inter-normal-13px">
                      <Link
                        className="font-semibold text-blue-600 transition-colors hover:text-blue-800 hover:underline"
                        title={`Click to find organization ${orgMap.get(workspace.organization_id)?.name || workspace.organization_id} in the Workspaces tab`}
                        to={`/admin-panel/organizations?search=${workspace.organization_id}`}
                      >
                        <HighlightText
                          searchTerm={searchTerm}
                          text={
                            orgMap.get(workspace.organization_id)?.name ||
                            workspace.organization_id.substring(0, 12) + "..."
                          }
                        />
                      </Link>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3">
                      <div>
                        <div className="font-inter-semibold-12px">
                          <Link
                            className="text-blue-600 transition-colors hover:text-blue-800 hover:underline"
                            title="Click to find this workspace in the Workspaces tab"
                            to={`/admin-panel/organizations?search=${workspace.id}`}
                          >
                            <HighlightText
                              searchTerm={searchTerm}
                              text={workspace.name}
                            />
                          </Link>
                        </div>
                        <div className="font-inter-normal-12px text-gray-500">
                          <HighlightText
                            searchTerm={searchTerm}
                            text={workspace.slug}
                          />
                        </div>
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3">
                      <span
                        className={`inline-flex items-center rounded-full px-3 py-1 font-inter-medium-13px ${getStatusColor(
                          module.status,
                        )}`}
                      >
                        {module.status}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 font-inter-normal-13px text-gray-900">
                      <code className="rounded bg-gray-200 px-2 py-1 font-code-12 text-gray-800">
                        {module.version}
                      </code>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 font-inter-normal-13px text-gray-500">
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => handleConfigureModule(workspace)}
                      >
                        <Icon icon={faCog} size="sm" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };
  let countText = "";
  if (searchTerm) {
    countText = `${filteredWorkspaces.length} workspaces match "${searchTerm}"`;
  } else if (selectedVersion) {
    countText = `${filteredWorkspaces.length} workspaces have version ${selectedVersion}`;
  } else {
    countText = `${workspacesWithModule.length} of ${workspaces.length} workspaces have this module created`;
  }

  return (
    <div className="space-y-6">
      <ModulesVersionBar
        allVersions={allVersions}
        moduleType={moduleType}
        selectedVersion={selectedVersion}
        workspaces={workspaces}
        onSelectVersion={setSelectedVersion}
      />

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="text-gray-500 text-sm">{countText}</div>
        </div>
        <div className="flex gap-3">
          <Button
            iconLeft={faPlus}
            size="sm"
            variant="primary"
            onClick={openCreateModal}
          >
            Create Module
          </Button>
        </div>
      </div>
      {renderContent()}

      {selectedWorkspace && selectedModule && (
        <ModuleConfigModal
          afterLeave={configAfterLeave}
          isOpen={configModalOpen}
          module={selectedModule}
          moduleType={moduleType}
          organization={orgMap.get(selectedWorkspace.organization_id)!}
          workspace={selectedWorkspace}
          onClose={handleModalClose}
        />
      )}

      <ModuleCreateModal
        afterLeave={afterLeave}
        isOpen={createModalOpen}
        moduleType={moduleType}
        orgMap={orgMap}
        workspaces={workspaces}
        onClose={closeCreateModal}
      />
    </div>
  );
};

type WorkspaceConfigRow = {
  workspaceId: string;
  workspaceName: string;
  workspaceSlug: string;
  organizationId: string;
  organizationName: string;
  status: ModuleStatus;
  version: string;
  [key: string]: string | number | boolean | undefined | ModuleStatus;
};

const columnHelper = createColumnHelper<WorkspaceConfigRow>();

const SortableHeader = <D extends object, V>({
  info,
  children,
  showFilter = false,
}: {
  info: Parameters<typeof Header<D, V>>[0]["info"];
  children: React.ReactNode;
  showFilter?: boolean;
}) => {
  const column = info.column;
  const isSorted = column.getIsSorted();
  const canSort = column.getCanSort();
  const filterValue = (column.getFilterValue() as string) ?? "";
  let sortIcon = faArrowsUpDown;
  if (isSorted === "asc") {
    sortIcon = faArrowUp;
  } else if (isSorted === "desc") {
    sortIcon = faArrowDown;
  }

  return (
    <div className="flex flex-col gap-2 px-4 py-3">
      <button
        className={`flex items-center gap-2 whitespace-nowrap text-left font-inter-medium-12px text-gray-800 ${
          canSort ? "cursor-pointer hover:text-gray-900" : ""
        }`}
        disabled={!canSort}
        type="button"
        onClick={() => canSort && column.toggleSorting()}
      >
        <span>{children}</span>
        {canSort && <Icon icon={sortIcon} size="2xs" />}
      </button>
      {showFilter && column.getCanFilter() && (
        <div className="min-w-[120px]">
          <Input
            placeholder="Filter..."
            type="text"
            value={filterValue}
            onChange={(e) => column.setFilterValue(e.target.value)}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

const AdvancedModeSection: React.FC<{
  moduleType: string;
  workspaces: WorkspaceDb[];
  orgMap: Map<string, OrganizationInDB>;
  searchTerm: string;
  allVersions: string[];
}> = ({ moduleType, workspaces, orgMap, searchTerm, allVersions }) => {
  const {
    isOpen: configModalOpen,
    openModal: openConfigModal,
    closeModal: closeConfigModal,
    afterLeave: configAfterLeave,
    data: selectedWorkspace,
  } = useModal<WorkspaceDb>();
  const [selectedModule, setSelectedModule] = useState<ModuleSpec | null>(null);
  const [selectedVersion, setSelectedVersion] = useState<string | null>(null);

  const handleConfigureModule = useCallback(
    (workspace: WorkspaceDb) => {
      const modules = workspace.modules as Record<string, ModuleSpec>;
      const module = modules?.[moduleType];
      if (module) {
        setSelectedModule(module);
        openConfigModal(workspace);
      }
    },
    [moduleType, openConfigModal, setSelectedModule],
  );

  const handleModalClose = () => {
    closeConfigModal();
    setSelectedModule(null);
  };

  // Get all workspaces with this module
  const workspacesWithModule = useMemo(() => {
    return workspaces.filter((ws) => {
      const modules = ws.modules as Record<string, ModuleSpec>;
      return modules?.[moduleType];
    });
  }, [workspaces, moduleType]);

  // Extract all unique config keys across all workspaces for this module
  const allConfigKeys = useMemo(() => {
    const keysSet = new Set<string>();
    workspacesWithModule.forEach((ws) => {
      const modules = ws.modules as Record<string, ModuleSpec>;
      const module = modules?.[moduleType];
      if (module?.config) {
        Object.keys(module.config).forEach((key) => keysSet.add(key));
      }
    });
    return Array.from(keysSet).sort();
  }, [workspacesWithModule, moduleType]);

  // Transform workspace data into table rows
  const tableData = useMemo(() => {
    return workspacesWithModule.map((ws) => {
      const modules = ws.modules as Record<string, ModuleSpec>;
      const module = modules?.[moduleType];
      const config = module?.config || {};

      const row: WorkspaceConfigRow = {
        workspaceId: ws.id,
        workspaceName: ws.name,
        workspaceSlug: ws.slug,
        organizationId: ws.organization_id,
        organizationName:
          orgMap.get(ws.organization_id)?.name ||
          ws.organization_id.substring(0, 12) + "...",
        status: module?.status || "unknown",
        version: module?.version || "",
      };

      // Add all config values as columns
      allConfigKeys.forEach((key) => {
        row[key] = config[key as keyof typeof config];
      });

      return row;
    });
  }, [workspacesWithModule, moduleType, orgMap, allConfigKeys]);

  // Define table columns
  const columns = useMemo(() => {
    const baseColumns = [
      columnHelper.accessor("organizationName", {
        id: "organizationName",
        header: (info) => (
          <SortableHeader info={info} showFilter>
            Organization
          </SortableHeader>
        ),
        cell: (info) => {
          const row = info.row.original;
          return (
            <Cell className="px-4" info={info}>
              <Link
                className="font-inter-semibold-13px text-blue-600 transition-colors hover:text-blue-800 hover:underline"
                title={`Click to find organization ${row.organizationName} in the Workspaces tab`}
                to={`/admin-panel/organizations?search=${row.organizationId}`}
              >
                {row.organizationName}
              </Link>
            </Cell>
          );
        },
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: "includesString",
        minSize: 180,
        meta: {
          displayName: "Organization",
        },
      }),
      columnHelper.accessor("workspaceName", {
        id: "workspaceName",
        header: (info) => (
          <SortableHeader info={info} showFilter>
            Workspace
          </SortableHeader>
        ),
        cell: (info) => {
          const row = info.row.original;
          return (
            <Cell className="px-4" info={info}>
              <Link
                className="font-inter-semibold-13px text-blue-600 transition-colors hover:text-blue-800 hover:underline"
                title="Click to find this workspace in the Workspaces tab"
                to={`/admin-panel/organizations?search=${row.workspaceId}`}
              >
                {row.workspaceName}
              </Link>
            </Cell>
          );
        },
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: "includesString",
        minSize: 200,
        meta: {
          displayName: "Workspace",
        },
      }),
      columnHelper.accessor("status", {
        id: "status",
        header: (info) => (
          <SortableHeader info={info} showFilter>
            Status
          </SortableHeader>
        ),
        cell: (info) => {
          const status = info.getValue();
          return (
            <Cell className="px-4" info={info}>
              <span
                className={`inline-flex items-center rounded-full px-3 py-1 font-inter-medium-12px ${getStatusColor(
                  status,
                )}`}
              >
                {status}
              </span>
            </Cell>
          );
        },
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: "includesString",
        minSize: 150,
        meta: {
          displayName: "Status",
        },
      }),
      columnHelper.accessor("version", {
        id: "version",
        header: (info) => (
          <SortableHeader info={info} showFilter>
            Version
          </SortableHeader>
        ),
        cell: (info) => (
          <Cell className="px-4" info={info}>
            <code className="rounded bg-gray-100 px-2 py-1 font-code-12 text-gray-800">
              {info.getValue()}
            </code>
          </Cell>
        ),
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: "includesString",
        minSize: 180,
        meta: {
          displayName: "Version",
        },
      }),
    ];

    const renderHeader = (info: HeaderContext<WorkspaceConfigRow, string>) => {
      return (
        <SortableHeader info={info} showFilter>
          {info.column.id
            .replace(/_/g, " ")
            .replace(/\b\w/g, (l) => l.toUpperCase())}
        </SortableHeader>
      );
    };

    // Add dynamic columns for each config key
    const configColumns = allConfigKeys.map((key) =>
      columnHelper.accessor(key, {
        id: key,
        header: renderHeader,
        cell: (info) => {
          const value = info.getValue();
          if (value === undefined || value === null) {
            return (
              <Cell className="px-4" info={info}>
                <span className="font-inter-normal-13px text-gray-400 italic">
                  -
                </span>
              </Cell>
            );
          }
          if (typeof value === "boolean") {
            return (
              <Cell className="px-4" info={info}>
                <Pill variant={value ? "green" : "red"}>
                  <Pill.Text fontType="text">{String(value)}</Pill.Text>
                </Pill>
              </Cell>
            );
          }
          return (
            <Cell className="px-4" info={info}>
              <span className="font-inter-normal-13px text-gray-900">
                {String(value)}
              </span>
            </Cell>
          );
        },
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: "includesString",
        minSize: 160,
        meta: {
          displayName: key
            .replace(/_/g, " ")
            .replace(/\b\w/g, (l) => l.toUpperCase()),
        },
      }),
    );

    // Add actions column
    const actionsColumn = columnHelper.display({
      id: "actions",
      header: (info) => <Header info={info}>Actions</Header>,
      cell: (info) => {
        const row = info.row.original;
        const workspace = workspacesWithModule.find(
          (ws) => ws.id === row.workspaceId,
        );
        return (
          <Cell className="px-4" info={info}>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => workspace && handleConfigureModule(workspace)}
            >
              <Icon icon={faCog} size="sm" />
            </Button>
          </Cell>
        );
      },
      minSize: 100,
      meta: {
        displayName: "Actions",
      },
    });

    return [...baseColumns, ...configColumns, actionsColumn];
  }, [allConfigKeys, workspacesWithModule, handleConfigureModule]);

  // Apply search and version filters
  const filteredData = useMemo(() => {
    let data = tableData;

    // Apply version filter
    if (selectedVersion) {
      data = data.filter((row) => row.version === selectedVersion);
    }

    // Apply search filter
    if (!searchTerm) return data;

    const searchLower = searchTerm.toLowerCase();
    return data.filter((row) => {
      return (
        row.workspaceName.toLowerCase().includes(searchLower) ||
        row.workspaceSlug.toLowerCase().includes(searchLower) ||
        row.workspaceId.toLowerCase().includes(searchLower) ||
        row.organizationId.toLowerCase().includes(searchLower) ||
        row.organizationName.toLowerCase().includes(searchLower) ||
        String(row.version).toLowerCase().includes(searchLower) ||
        String(row.status).toLowerCase().includes(searchLower)
      );
    });
  }, [tableData, searchTerm, selectedVersion]);

  if (workspacesWithModule.length === 0) {
    return (
      <div className="overflow-hidden rounded-lg border border-gray-200 bg-gray-50 shadow-sm">
        <EmptyState
          description="No workspaces have this module created yet."
          headline="No workspaces found"
          icon={faServer}
        />
      </div>
    );
  }

  let countText = "";
  if (searchTerm) {
    countText = `${filteredData.length} workspaces match "${searchTerm}"`;
  } else if (selectedVersion) {
    countText = `${filteredData.length} workspaces have version ${selectedVersion}`;
  } else {
    countText = `${workspacesWithModule.length} of ${workspaces.length} workspaces have this module created`;
  }
  return (
    <div className="space-y-6">
      <ModulesVersionBar
        allVersions={allVersions}
        moduleType={moduleType}
        selectedVersion={selectedVersion}
        workspaces={workspaces}
        onSelectVersion={setSelectedVersion}
      />

      <div className="flex items-center justify-between">
        <div className="text-gray-500 text-sm">{countText}</div>
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        <TableComp
          columns={columns}
          data={filteredData}
          frameClassName="max-h-[700px]"
          getRowId={(row) => row.workspaceId}
          variant="default"
          clientSideFiltering
        />
      </div>

      {selectedWorkspace && selectedModule && (
        <ModuleConfigModal
          afterLeave={configAfterLeave}
          isOpen={configModalOpen}
          module={selectedModule}
          moduleType={moduleType}
          organization={orgMap.get(selectedWorkspace.organization_id)!}
          workspace={selectedWorkspace}
          onClose={handleModalClose}
        />
      )}
    </div>
  );
};

export const ModulesSettings: React.FC = () => {
  const workspacesQuery = useWorkspaces();
  const organizationsQuery = useUserOrganizations();
  const [selectedModuleTab, setSelectedModuleTab] = useState<string>("");

  const [searchParams, setSearchParams] = useSearchParams();
  const searchInputRef = useRef<HTMLInputElement>(null);

  const searchTerm = searchParams.get("search") || "";

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setSearchParams(
      (prev) => {
        if (newValue) {
          prev.set("search", newValue);
        } else {
          prev.delete("search");
        }
        return prev;
      },
      { replace: true },
    );
  };

  const handleTabChange = useCallback(
    (tabKey: string) => {
      setSelectedModuleTab(tabKey);
      setSearchParams(
        (prev) => {
          prev.set("type", tabKey);
          return prev;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // Sync selected tab with URL (?type=MODULE_TYPE) and set a default if missing
  useEffect(() => {
    const workspaces = workspacesQuery.data as WorkspaceDb[] | undefined;
    if (!workspaces || workspaces.length === 0) return;

    const allModuleTypes = Array.from(
      new Set(workspaces.flatMap((ws) => Object.keys(ws.modules || {}))),
    );
    if (allModuleTypes.length === 0) return;

    const urlType = searchParams.get("type") || "";

    // If URL has a valid type and it's different from state, sync it
    if (
      urlType &&
      allModuleTypes.includes(urlType) &&
      urlType !== selectedModuleTab
    ) {
      setSelectedModuleTab(urlType);
      return;
    }

    // If no selected tab yet, choose first and write it to URL
    if (!selectedModuleTab) {
      const defaultType = allModuleTypes[0];
      setSelectedModuleTab(defaultType);
      if (urlType !== defaultType) {
        setSearchParams(
          (prev) => {
            prev.set("type", defaultType);
            return prev;
          },
          { replace: true },
        );
      }
    }
  }, [searchParams, selectedModuleTab, setSearchParams, workspacesQuery.data]);

  const [advancedMode, setAdvancedMode] = useState(false);

  return (
    <LoadingView
      queryResult={[workspacesQuery, organizationsQuery]}
      renderUpdated={([workspaces, organizations]: [
        WorkspaceDb[],
        OrganizationInDB[],
      ]) => {
        // Create organization lookup map
        const orgMap = new Map(organizations.map((org) => [org.id, org]));

        // Get all unique module types across all workspaces
        const allModuleTypes = Array.from(
          new Set(workspaces.flatMap((ws) => Object.keys(ws.modules || {}))),
        );

        // Get all unique versions across all modules
        const allVersions = Array.from(
          new Set(
            workspaces.flatMap((ws) => {
              const modules = ws.modules as Record<string, ModuleSpec>;
              return Object.values(modules || {})
                .map((module) => module?.version)
                .filter(
                  (v): v is string => typeof v === "string" && v.length > 0,
                );
            }),
          ),
        );

        if (allModuleTypes.length === 0) {
          return (
            <div className="min-h-screen bg-gray-50 p-8">
              <div className="mx-auto max-w-7xl">
                <div className="mb-8">
                  <h1 className="font-inter-semibold-24px text-gray-900">
                    Workspace Modules
                  </h1>
                  <p className="mt-2 font-inter-normal-13px text-gray-600">
                    Manage and monitor modules across all {workspaces.length}{" "}
                    workspaces
                  </p>
                </div>

                <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
                  <EmptyState
                    description="No modules are created across any workspaces yet."
                    headline="No modules found"
                    icon={faServer}
                  />
                </div>
              </div>
            </div>
          );
        }

        return (
          <div className="min-h-screen bg-gray-50 p-8">
            <div className="mx-auto max-w-7xl">
              <div className="mb-8 flex items-start justify-between">
                <div>
                  <h1 className="font-inter-semibold-32px text-gray-900">
                    Workspace Modules
                  </h1>
                  <p className="mt-2 font-inter-normal-16px text-gray-600">
                    Manage and monitor modules across all {workspaces.length}{" "}
                    workspaces
                  </p>
                </div>
                <div className="flex items-center gap-3 rounded-lg px-4 py-3 shadow-sm">
                  <div className="flex flex-col gap-0.5">
                    <span className="font-inter-semibold-13px text-gray-900">
                      Advanced Mode
                    </span>
                    <span className="font-inter-normal-12px text-gray-500">
                      Show all config values in table
                    </span>
                  </div>
                  <Switch
                    enabled={advancedMode}
                    onChange={() => setAdvancedMode(!advancedMode)}
                  />
                </div>
              </div>

              {/* Search Bar */}
              <div className="mb-6">
                <Input
                  ref={searchInputRef}
                  placeholder="Search workspaces, organizations, IDs... (⌘F)"
                  type="text"
                  value={searchTerm}
                  fullWidth
                  onChange={handleSearchChange}
                />
              </div>

              <Tabs
                containerClassName="bg-white rounded-xl border border-gray-200 shadow-sm"
                panelsClassName="p-6"
                selectedKey={selectedModuleTab || allModuleTypes[0]}
                tabClassName="border-b border-gray-200 px-6"
                tabs={allModuleTypes.map((moduleType) => ({
                  key: moduleType,
                  label: (
                    <div className="flex items-center gap-2">
                      <span>{getModuleDisplayName(moduleType)}</span>
                    </div>
                  ),
                  content: advancedMode ? (
                    <AdvancedModeSection
                      allVersions={allVersions}
                      moduleType={moduleType}
                      orgMap={orgMap}
                      searchTerm={searchTerm}
                      workspaces={workspaces}
                    />
                  ) : (
                    <ModuleSection
                      allVersions={allVersions}
                      moduleType={moduleType}
                      orgMap={orgMap}
                      searchTerm={searchTerm}
                      workspaces={workspaces}
                    />
                  ),
                }))}
                onChange={handleTabChange}
              />
            </div>
          </div>
        );
      }}
    />
  );
};
